import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine, Reveal } from "./Reveal";
import { IMAGES } from "@/lib/apiClient";
import { ArrowDown, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/apiClient";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "42%"]);

  const go = (href) => scrollToId(href);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[720px] w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Fountain of living water in a cosmic canyon" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[#05030A]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05030A]/70 via-transparent to-[#05030A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05030A]/80 via-transparent to-[#05030A]/40" />
      </motion.div>

      {/* Glow orbs */}
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#6A0DAD]/25 blur-[130px] animate-pulse-glow" />
      <div className="absolute right-0 bottom-0 w-[420px] h-[420px] rounded-full bg-[#00E5FF]/12 blur-[120px]" />

      <motion.div style={{ y: yContent, opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <MaskLine delay={0.4}>
          <span className="section-label flex items-center gap-3">
            <Sparkles className="w-3.5 h-3.5" /> The World's First Authentic Intelligent (Christ-Like) Apostolic Church
          </span>
        </MaskLine>

        <h1 className="font-display font-light leading-[0.92] tracking-tight mt-6 text-white text-[16vw] sm:text-[12vw] lg:text-[9.5rem]">
          <MaskLine delay={0.55}><span className="text-white text-glow-gold">Rebirth.</span></MaskLine>
          <MaskLine delay={0.7}><span className="italic text-[#B4ADC1]">Refresh.</span></MaskLine>
          <MaskLine delay={0.85}><span className="text-gold text-glow-gold">Renew.</span></MaskLine>
        </h1>

        <Reveal delay={1.15} className="mt-8 max-w-xl">
          <p className="text-lg md:text-xl font-light text-[#C9C3D4] leading-relaxed">
            <span className="text-white">"If any man thirst, let him come unto me, and drink."</span> Fountain of Living Water Apostolic Church — where thirsty souls encounter Jesus through nine authentic men & women of God, apostolic truth, and compassionate family.
          </p>
        </Reveal>

        <Reveal delay={1.35} className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            data-testid="hero-cta-ministers"
            onClick={() => go("#ministers")}
            className="group px-8 py-4 rounded-full bg-gold text-black font-medium hover:bg-[#e6c14e] transition-colors duration-300 flex items-center justify-center gap-2"
          >
            Speak With a Minister
            <span className="w-2 h-2 rounded-full bg-black/70 group-hover:scale-150 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-cta-water"
            onClick={() => go("#connect")}
            className="px-8 py-4 rounded-full glass text-white font-light hover:border-white/25 transition-colors duration-300"
          >
            Come to the Water
          </button>
        </Reveal>
      </motion.div>

      <motion.button
        onClick={() => go("#ministers")}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#7A708A]"
        data-testid="hero-scroll"
      >
        <span className="font-mono text-[0.6rem] tracking-[0.3em] uppercase">Descend</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
